<template>
  <v-form v-on:submit.prevent="submit" novalidate class="p-sign-up_second-step">
    <v-row>
      <v-col cols="6" class="p-sign-up_second-step_col">
        <BaseInput
          v-model="firstName"
          :errors="firstNameErrors"
          fieldName="firstName"
          :placeholder="$t('general.inputs.firstName')"
          @blur="$v.firstName.$touch()"
          @change="onInputChange"
        />
      </v-col>
      <v-col cols="6" class="p-sign-up_second-step_col">
        <BaseInput
          v-model="lastName"
          :errors="lastNameErrors"
          fieldName="lastName"
          :placeholder="$t('general.inputs.lastName')"
          @blur="$v.lastName.$touch()"
          @change="onInputChange"
        />
      </v-col>
    </v-row>
    <DatePicker
      v-model="dateOfBirth"
      :dense="true"
      height="47"
      :errors="dateOfBirthErrors"
      :placeholder="$t('general.inputs.dateOfBirth')"
      :maxDate="new Date().toISOString().substr(0, 10)"
      @blur="$v.dateOfBirth.$touch()"
    />
    <Switcher v-model="gender" :items="$t('general.genders')" @change="onGenderChange" />
    <StateSelect
      :errors="selectedStateErrors"
      v-model="selectedState"
      @blur="$v.selectedState.$touch()"
      @change="onStateChange"
    />
    <PhoneInput
      v-model="homePhoneNumber"
      fieldName="homePhoneNumber"
      placeholder="Home phone number"
      @change="onInputChange"
    />
    <PhoneInput
      v-model="phoneNumber"
      fieldName="phoneNumber"
      :placeholder="'Mobile phone number ' + $t('general.inputs.optional')"
      @change="onInputChange"
    />
    <BaseInput
      v-model="address"
      fieldName="address"
      :placeholder="$t('general.inputs.address')"
      @change="onInputChange"
      :persistentHint="true"
    />
    <FileInput
      v-model="idFileDriverLicenseFront"
      fieldName="idFileDriverLicenseFront"
      @change="onInputChange"
      :placeholder="$t('general.inputs.driverLicenseIdFront')"
    />
    <FileInput
      v-model="idFileDriverLicenseBack"
      fieldName="idFileDriverLicenseBack"
      @change="onInputChange"
      :placeholder="$t('general.inputs.driverLicenseIdBack')"
    />
    <v-btn
      type="submit"
      depressed
      large
      width="100%"
      color="success"
      :class="{
        'p-sign-up_second-step_btn': true,
        'p-sign-up_second-step_btn__disabled': this.$v.$invalid,
      }"
      :light="this.$v.$invalid"
      :dark="!this.$v.$invalid"
      :disabled="this.$v.$invalid"
    >
      {{ this.$t("registration.step2.form.button") }}
    </v-btn>
  </v-form>
</template>
<script>
import { required } from "vuelidate/lib/validators";
import { mapGetters } from "vuex";

import DatePicker from "../shared/DatePicker";
import PhoneInput from "../shared/PhoneInput";
import StateSelect from "../shared/StateSelect";
import BaseInput from "../uikit/BaseInput";
import FileInput from "../uikit/FileInput";
import Switcher from "../uikit/Switcher";

export default {
  name: "PatientSignUpSecondStepForm",
  components: {
    DatePicker,
    BaseInput,
    PhoneInput,
    StateSelect,
    Switcher,
    FileInput,
  },
  validations: {
    selectedState: { required },
    dateOfBirth: { required },
    firstName: { required },
    lastName: { required },
    idFileDriverLicenseFront: { required },
    idFileDriverLicenseBack: { required },
    homePhoneNumber: { required },
  },
  data: () => ({
    selectedState: "",
    dateOfBirth: null,
    firstName: "",
    lastName: "",
    homePhoneNumber: "",
    phoneNumber: "",
    gender: "other",
    address: "",
    idFileDriverLicenseFront: {},
    idFileDriverLicenseBack: {},
  }),
  computed: {
    ...mapGetters("invites", ["invite"]),
    firstNameErrors() {
      const errors = [];
      if (!this.$v.firstName.$dirty) return errors;
      !this.$v.firstName.required &&
        errors.push(this.$t("validation.required", { field: this.$t("general.inputs.firstName") }));
      return errors;
    },
    lastNameErrors() {
      const errors = [];
      if (!this.$v.lastName.$dirty) return errors;
      !this.$v.lastName.required &&
        errors.push(this.$t("validation.required", { field: this.$t("general.inputs.lastName") }));
      return errors;
    },
    selectedStateErrors() {
      const errors = [];
      if (!this.$v.selectedState.$dirty) return errors;
      !this.$v.selectedState.required &&
        errors.push(this.$t("validation.required", { field: this.$t("general.inputs.state") }));
      return errors;
    },
    dateOfBirthErrors() {
      const errors = [];
      if (!this.$v.dateOfBirth.$dirty) return errors;
      !this.$v.dateOfBirth.required &&
        errors.push(this.$t("validation.required", { field: this.$t("general.inputs.dateOfBirth") }));
      return errors;
    },
    homePhoneNumber() {
      const errors = [];
      if (!this.$v.homePhoneNumber.$dirty) return errors;
      !this.$v.homePhoneNumber.required && errors.push(this.$t("validation.required", { field: "Home phone number" }));
      return errors;
    },
  },

  methods: {
    submit() {
      this.$emit("checkoutStep", 3);
      this.$emit("saveValues", this._data);
    },
    onInputChange(value, fieldName) {
      this[fieldName] = value;
    },
    onStateChange(state) {
      this.selectedState = state;
    },
    onGenderChange(gender) {
      this.gender = gender;
    },
  },
  mounted() {
    this.firstName = this.invite.firstName || "";
    this.lastName = this.invite.lastName || "";
    if (this.invite.birthDate) {
      this.dateOfBirth = new Date(this.invite.birthDate);
    }
  },
};
</script>
<style lang="scss">
.v-input__control {
  .v-input__slot {
    padding: 4px 12px 2px 12px;
  }
}
.p-sign-up_second-step {
  .v-input__slot {
    border: 1px solid $primarylight2;
  }

  &_select {
    .theme--light.v-icon {
      color: $icon;
    }
  }
  &_btn {
    text-transform: capitalize;
    font-style: normal;
    font-weight: 500;
    font-size: 1rem !important;
    line-height: 20px;

    &__disabled {
      color: $primaryblack;
      .theme--light.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
        background-color: $primarylight !important;
      }
    }
  }
  &_col {
    padding-top: 0;
    padding-bottom: 0;
  }
  &_btn-toggle {
    height: 47px;
    margin-bottom: 29px;

    &_btn {
      flex: 0 0 34%;
    }
  }
}
</style>
